import '../../styles/fonts.css';
import React from 'react';
import { PixelButton, PixelContainer } from './PixelComponents';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <PixelContainer
        style={{
          backgroundColor: '#fff',
          padding: '16px',
          textAlign: 'center',
          fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
          fontSize: '14px',
          width: '300px',
        }}
      >
        {title && <h3 className='text-lg font-bold mb-4'>{title}</h3>}
        {children}
      </PixelContainer>
    </div>
  );
};

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: string;
  cancelColor?: string;
  icon?: React.ReactNode;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  confirmColor,
  cancelColor,
  title,
  message,
  confirmText = 'ДА',
  cancelText = 'НЕТ',
  icon,
}) => {
  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <PixelContainer
        style={{
          backgroundColor: '#fff',
          padding: '16px',
          textAlign: 'center',
          fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
          fontSize: '14px',
          width: '300px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {icon && <div style={{ marginBottom: '8px' }}>{icon}</div>}
        <p>{message}</p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '24px',
            width: '100%',
            marginTop: '16px',
          }}
        >
          <PixelButton
            onClick={onClose}
            style={{
              flex: 1,
              width: '120px',
              height: '32px',
              fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
              fontWeight: 400,
              color: cancelColor,
            }}
          >
            {cancelText}
          </PixelButton>
          <PixelButton
            onClick={onConfirm}
            style={{
              flex: 1,
              width: '120px',
              height: '32px',
              fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
              fontWeight: 400,
              color: confirmColor,
            }}
          >
            {confirmText}
          </PixelButton>
        </div>
      </PixelContainer>
    </div>
  );
};

interface InputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (value: string) => void;
  title: string;
  placeholder?: string;
  maxLength?: number;
  inputType?: 'text' | 'number';
  confirmText?: string;
  cancelText?: string;
}

export const InputModal: React.FC<InputModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  placeholder = '',
  maxLength,
  inputType = 'text',
  confirmText = 'ОК',
  cancelText = 'ОТМЕНА',
}) => {
  const [value, setValue] = React.useState('');

  const handleConfirm = () => {
    onConfirm(value);
    setValue('');
    onClose();
  };

  const handleClose = () => {
    setValue('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <PixelContainer
        style={{
          backgroundColor: '#fff',
          padding: '16px',
          textAlign: 'center',
          fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
          fontSize: '14px',
          width: '300px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <p>{title}</p>
        <PixelContainer
          style={{
            padding: '0',
            backgroundColor: '#F0F0F0',
            height: '40px',
          }}
        >
          <input
            type={inputType}
            maxLength={maxLength}
            placeholder={placeholder}
            value={value}
            onChange={(e) => {
              const newValue = e.target.value;
              if (!maxLength || newValue.length <= maxLength) {
                setValue(newValue);
              }
            }}
            style={{
              width: '100%',
              padding: '10px',
              height: '40px',
              border: 'none',
              fontFamily: 'Roboto Mono, monospace',
              fontSize: '18px',
              fontWeight: 'bold',
              letterSpacing: '2px',
              backgroundColor: 'transparent',
              textAlign: 'center',
              outline: 'none',
            }}
          />
        </PixelContainer>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '24px',
            width: '100%',
            marginTop: '16px',
          }}
        >
          <PixelButton
            onClick={handleClose}
            style={{
              flex: 1,
              width: '120px',
              height: '32px',
              fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
            }}
          >
            {cancelText}
          </PixelButton>
          <PixelButton
            onClick={handleConfirm}
            style={{
              flex: 1,
              width: '120px',
              height: '32px',
              fontFamily: 'Tiny5, "Courier New", monospace, sans-serif',
            }}
          >
            {confirmText}
          </PixelButton>
        </div>
      </PixelContainer>
    </div>
  );
};
